import React from 'react';
import { FiHexagon } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Polygon } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const PolygonBasicsVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Polygon Examples
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-5, 5], y: [-3, 3] }}>
      <Coordinates.Cartesian />

      {/* Triangle */}
      <Polygon
        points={[
          [-4, -1],
          [-4, 1],
          [-2, 2],
        ]}
        color="#ef4444"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-3} y={-2} color="#dc2626" size={14} weight="bold">
        Triangle
      </Text>
      <Text x={-3} y={-2.5} color="#1f2937" size={12}>
        3 sides
      </Text>

      {/* Square */}
      <Polygon
        points={[
          [-1, -1],
          [1, -1],
          [1, 1],
          [-1, 1],
        ]}
        color="#10b981"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-0.5} y={-2} color="#059669" size={14} weight="bold">
        Square
      </Text>
      <Text x={-0.5} y={-2.5} color="#1f2937" size={12}>
        4 sides
      </Text>

      {/* Pentagon */}
      <Polygon
        points={[
          [2.5, -1],
          [4, -0.5],
          [4, 1],
          [3, 2],
          [2, 1],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={2.5} y={-2} color="#1d4ed8" size={14} weight="bold">
        Pentagon
      </Text>
      <Text x={2.5} y={-2.5} color="#1f2937" size={12}>
        5 sides
      </Text>
    </Mafs>
  </div>
);

const AngleSumVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Interior Angle Sum
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-3, 3], y: [-2, 2] }}>
      <Coordinates.Cartesian />

      {/* Pentagon example */}
      <Polygon
        points={[
          [-1.5, -1],
          [0, -1.5],
          [1.5, -1],
          [1, 1],
          [-1, 1],
        ]}
        color="#2563eb"
        fillOpacity={0.2}
        strokeWidth={2}
      />

      {/* Angle markers at vertices */}
      <Point x={-1.5} y={-1} color="#ef4444" />
      <Point x={0} y={-1.5} color="#ef4444" />
      <Point x={1.5} y={-1} color="#ef4444" />
      <Point x={1} y={1} color="#ef4444" />
      <Point x={-1} y={1} color="#ef4444" />

      {/* Formula below */}
      <Text x={-1} y={-2} color="#1f2937" size={14} weight="bold">
        Pentagon: (5-2) × 180° = 540°
      </Text>
      <Text x={-1} y={-2.3} color="#1f2937" size={12}>
        n = 5 sides
      </Text>
    </Mafs>
  </div>
);

const RegularPolygonVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Regular Hexagon
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-3, 3], y: [-2.5, 2.5] }}>
      <Coordinates.Cartesian />

      {/* Regular hexagon */}
      <Polygon
        points={[
          [2, 0],
          [1, Math.sqrt(3)],
          [-1, Math.sqrt(3)],
          [-2, 0],
          [-1, -Math.sqrt(3)],
          [1, -Math.sqrt(3)],
        ]}
        color="#10b981"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Center point */}
      <Point x={0} y={0} color="#ef4444" />

      {/* Lines from center to vertices */}
      <Plot.OfX
        y={(x) => Math.sqrt(3) * x}
        color="#ef4444"
        strokeWidth={1}
        strokeDasharray="3,3"
        domain={[0, 1]}
      />
      <Plot.OfX
        y={(x) => -Math.sqrt(3) * x}
        color="#ef4444"
        strokeWidth={1}
        strokeDasharray="3,3"
        domain={[0, 1]}
      />

      {/* Labels */}
      <Text x={0} y={-2.5} color="#1f2937" size={14} weight="bold">
        6 equal sides, 6 equal angles
      </Text>
      <Text x={0} y={-2.8} color="#1f2937" size={12}>
        Each angle = 120°
      </Text>
    </Mafs>
  </div>
);

const PerimeterAreaVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Rectangle Area & Perimeter
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-3, 3], y: [-2, 2] }}>
      <Coordinates.Cartesian />

      {/* Rectangle */}
      <Polygon
        points={[
          [-2, -1],
          [2, -1],
          [2, 1],
          [-2, 1],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Dimension labels */}
      <Text x={0} y={-1.5} color="#dc2626" size={14} weight="bold">
        length = 4
      </Text>
      <Text x={-2.5} y={0} color="#059669" size={14} weight="bold">
        width = 2
      </Text>

      {/* Formulas */}
      <Text x={-1} y={1.5} color="#1f2937" size={14} weight="bold">
        Area = l × w = 4 × 2 = 8
      </Text>
      <Text x={-1} y={1.8} color="#1f2937" size={13}>
        Perimeter = 2(l + w) = 2(4 + 2) = 12
      </Text>
    </Mafs>
  </div>
);

const PolygonsLesson = () => {
  const sections = [
    {
      id: 'polygon-basics',
      title: 'Polygon Basics',
      content: {
        introduction:
          'A polygon is a closed figure made up of line segments. Polygons are classified by their number of sides and properties.',

        concepts: [
          {
            title: 'Types of Polygons',
            explanation:
              'Polygons are named by their number of sides: triangle (3), quadrilateral (4), pentagon (5), hexagon (6), heptagon (7), octagon (8).',
            visual: <PolygonBasicsVisual />,
            example: {
              problem: 'What is the name of a polygon with 8 sides?',
              solution: [
                'Count the number of sides: 8',
                'A polygon with 8 sides is called an octagon',
                'Common polygons: triangle (3), quadrilateral (4), pentagon (5), hexagon (6)',
                "The prefix 'octa-' means eight",
              ],
            },
          },
        ],
      },
    },
    {
      id: 'interior-angles',
      title: 'Interior Angles',
      content: {
        introduction:
          'The sum of interior angles in any polygon follows a specific formula based on the number of sides.',

        concepts: [
          {
            title: 'Angle Sum Formula',
            explanation:
              'The sum of interior angles in an n-sided polygon is §(n-2) \\times 180°§. This comes from dividing the polygon into triangles.',
            visual: <AngleSumVisual />,
            example: {
              problem: 'Find the sum of interior angles in a hexagon',
              solution: [
                'Identify the number of sides: n = 6',
                'Use the formula: §(n-2) \\times 180°§',
                'Substitute: §(6-2) \\times 180° = 4 \\times 180°§',
                'Sum = 720°',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'regular-polygons',
      title: 'Regular Polygons',
      content: {
        introduction:
          'Regular polygons have all sides equal and all angles equal. They have special properties and symmetries.',

        concepts: [
          {
            title: 'Regular Polygon Properties',
            explanation:
              'In a regular n-sided polygon, each interior angle measures §(n-2) \\times 180° \\div n§. Each exterior angle measures §360° \\div n§.',
            visual: <RegularPolygonVisual />,
            example: {
              problem: 'Find each interior angle of a regular octagon',
              solution: [
                'Use formula for regular polygon: §(n-2) \\times 180° \\div n§',
                'For octagon: n = 8',
                'Each angle = §(8-2) \\times 180° \\div 8 = 6 \\times 180° \\div 8§',
                'Each angle = §1080° \\div 8 = 135°§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'perimeter-area',
      title: 'Perimeter & Area',
      content: {
        introduction:
          'Perimeter is the distance around a polygon. Area is the space inside. Different polygons have different area formulas.',

        concepts: [
          {
            title: 'Basic Calculations',
            explanation:
              'Perimeter = sum of all side lengths. For rectangles: Area = §\\text{length} \\times \\text{width}§. For triangles: Area = §½ \\times \\text{base} \\times \\text{height}§.',
            visual: <PerimeterAreaVisual />,
            example: {
              problem:
                'A rectangle has length 7 and width 3. Find its perimeter and area.',
              solution: [
                'Perimeter = §2(\\text{length} + \\text{width}) = 2(7 + 3) = 2(10) = 20§',
                'Area = §\\text{length} \\times \\text{width} = 7 \\times 3 = 21§',
                'The rectangle has perimeter 20 and area 21',
                'Units: if length is in cm, perimeter is in cm and area is in cm²',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the sum of interior angles in a pentagon?',
      options: ['540°', '720°', '360°', '180°', '900°'],
      correct: 0,
      explanation:
        'Pentagon has 5 sides: §(5-2) \\times 180° = 3 \\times 180° = 540°§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'Each interior angle of a regular hexagon measures:',
      options: ['120°', '108°', '135°', '90°', '60°'],
      correct: 0,
      explanation:
        'Regular hexagon: §(6-2) \\times 180° \\div 6 = 720° \\div 6 = 120°§',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'A rectangle has length 8 and width 5. What is its area?',
      options: ['40', '26', '13', '15', '35'],
      correct: 0,
      explanation:
        'Area of rectangle = §\\text{length} \\times \\text{width} = 8 \\times 5 = 40§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'A regular octagon has how many lines of symmetry?',
      options: ['8', '4', '16', '6', '10'],
      correct: 0,
      explanation:
        'A regular octagon has 8 lines of symmetry — 4 through pairs of opposite vertices and 4 through midpoints of opposite sides.',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'The perimeter of a square is 24. What is its area?',
      options: ['36', '144', '24', '6', '48'],
      correct: 0,
      explanation:
        'If perimeter = 24, then each side = §24 \\div 4 = 6§. Area = §6² = 36§',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'A regular pentagon has each exterior angle equal to what measure?',
      options: ['72°', '60°', '108°', '90°', '120°'],
      correct: 0,
      explanation:
        'Sum of exterior angles of any polygon = 360°. Regular pentagon: 5 equal exterior angles. Each = §360°/5 = 72°§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'If a regular octagon has a side length of 5 cm, what is its perimeter?',
      options: ['40 cm', '35 cm', '45 cm', '30 cm', '50 cm'],
      correct: 0,
      explanation:
        'Regular octagon has 8 equal sides. Perimeter = §8 \\times 5 = 40§ cm.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is a polygon with 7 sides called?',
      options: ['Heptagon', 'Hexagon', 'Octagon', 'Pentagon', 'Nonagon'],
      correct: 0,
      explanation: 'A polygon with 7 sides is called a heptagon',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'The sum of exterior angles of any polygon is:',
      options: ['360°', '180°', '540°', '720°', 'Depends on number of sides'],
      correct: 0,
      explanation: 'The sum of exterior angles of any polygon is always 360°',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'In a regular polygon, if each interior angle is 144°, how many sides does it have?',
      options: ['10', '8', '12', '9', '6'],
      correct: 0,
      explanation:
        'Each exterior angle = §180° - 144° = 36°§. Number of sides = §360° \\div 36° = 10§ sides',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'A parallelogram has opposite sides that are:',
      options: [
        'Equal and parallel',
        'Equal only',
        'Parallel only',
        'Perpendicular',
        'None of these',
      ],
      correct: 0,
      explanation:
        'In a parallelogram, opposite sides are both equal in length and parallel to each other',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'Which shape has the largest area for a given perimeter?',
      options: [
        'Circle',
        'Square',
        'Regular hexagon',
        'Equilateral triangle',
        'Rectangle',
      ],
      correct: 0,
      explanation:
        'Among all shapes with the same perimeter, a circle has the largest area (isoperimetric inequality). Among polygons, more sides means closer to a circle.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'How many diagonals does a hexagon have?',
      options: ['9', '6', '12', '15', '8'],
      correct: 0,
      explanation:
        'Formula: §n(n-3)/2§ where n = 6. Diagonals = §6(6-3)/2 = 6 \\times 3/2 = 9§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'A square has a perimeter of 48 cm. What is its area?',
      options: ['144 cm²', '576 cm²', '192 cm²', '96 cm²', '288 cm²'],
      correct: 0,
      explanation:
        'Perimeter = §4s = 48§, so §s = 12§ cm. Area = §s² = 12² = 144§ cm².',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'An equilateral triangle has a perimeter of 24 cm. What is the length of one side?',
      options: ['8 cm', '6 cm', '12 cm', '4 cm', '10 cm'],
      correct: 0,
      explanation: 'Equilateral triangle: 3 equal sides. Side = §24/3 = 8§ cm.',
    },
    {
      type: 'untimed',
      question: 'What is a polygon with 7 sides called?',
      options: ['Heptagon', 'Hexagon', 'Octagon', 'Pentagon', 'Nonagon'],
      correct: 0,
      explanation: 'A polygon with 7 sides is called a heptagon',
    },
    {
      type: 'untimed',
      question: 'The sum of exterior angles of any polygon is:',
      options: ['360°', '180°', '540°', '720°', 'Depends on number of sides'],
      correct: 0,
      explanation: 'The sum of exterior angles of any polygon is always 360°',
    },
    {
      type: 'untimed',
      question:
        'In a regular polygon, if each interior angle is 144°, how many sides does it have?',
      options: ['10', '8', '12', '9', '6'],
      correct: 0,
      explanation:
        'Each exterior angle = §180° - 144° = 36°§. Number of sides = §360° \\div 36° = 10§ sides',
    },
    {
      type: 'untimed',
      question: 'A parallelogram has opposite sides that are:',
      options: [
        'Equal and parallel',
        'Equal only',
        'Parallel only',
        'Perpendicular',
        'None of these',
      ],
      correct: 0,
      explanation:
        'In a parallelogram, opposite sides are both equal in length and parallel to each other',
    },
    {
      type: 'untimed',
      question: 'Which shape has the largest area for a given perimeter?',
      options: [
        'Circle',
        'Square',
        'Regular hexagon',
        'Equilateral triangle',
        'Rectangle',
      ],
      correct: 0,
      explanation:
        'Among all shapes with the same perimeter, a circle has the largest area (isoperimetric inequality). Among polygons, more sides means closer to a circle.',
    },
    {
      type: 'untimed',
      question: 'How many diagonals does a hexagon have?',
      options: ['9', '6', '12', '15', '8'],
      correct: 0,
      explanation:
        'Formula: §n(n-3)/2§ where n = 6. Diagonals = §6(6-3)/2 = 6 \\times 3/2 = 9§.',
    },
    {
      type: 'untimed',
      question: 'A square has a perimeter of 48 cm. What is its area?',
      options: ['144 cm²', '576 cm²', '192 cm²', '96 cm²', '288 cm²'],
      correct: 0,
      explanation:
        'Perimeter = §4s = 48§, so §s = 12§ cm. Area = §s² = 12² = 144§ cm².',
    },
    {
      type: 'untimed',
      question:
        'An equilateral triangle has a perimeter of 24 cm. What is the length of one side?',
      options: ['8 cm', '6 cm', '12 cm', '4 cm', '10 cm'],
      correct: 0,
      explanation: 'Equilateral triangle: 3 equal sides. Side = §24/3 = 8§ cm.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'Compare the two quantities.',
      quantityA: 'Interior angle sum of a pentagon',
      quantityB: '§540§ degrees',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which formulas are correct for polygons? Select all that apply.',
      options: [
        'Interior sum: §(n-2)180§',
        'Exterior angle sum: §360§',
        'One interior angle of any polygon: §180/n§',
        'Number of diagonals: §n(n-3)/2§',
        'Interior sum: §n*180§',
      ],
      correct: [0, 1, 3],
      explanation:
        'The standard formulas are interior sum §(n-2)180§, exterior sum §360§, and diagonals §n(n-3)/2§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'How many diagonals does a hexagon have?',
      correctAnswer: '9',
      acceptedAnswers: ['9'],
      explanation: 'Use §n(n-3)/2 = 6*3/2 = 9§.',
    },
  ];

  const quickReference = [
    'Interior angles sum: §(n-2) \\times 180°§',
    'Regular polygon angle: §(n-2) \\times 180° \\div n§',
    'Exterior angles sum: 360°',
    'Rectangle area: §\\text{length} \\times \\text{width}§',
    'Number of diagonals: §n(n-3)/2§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Polygon concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Polygon concepts for the GRE Quantitative section',
    aicontext:
      'Master polygon properties, interior/exterior angles, regular polygons, and area calculations',
  };

  return (
    <LessonTemplate
      lessonId="polygons"
      lessonTitle="Polygons"
      lessonIcon={<FiHexagon />}
      lessonDescription="Master polygon properties, interior angles, regular polygons, perimeter, and area"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="polygons" />}
    />
  );
};

export default PolygonsLesson;
